import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { Box } from '@mui/system';
import {
  adminActions,
  adminGetListOfPharmacies,
  adminGetListOfPharmaciesPagination,
  AdminLegendVariant,
  adminListOfPharmacies,
  Agencies,
  getPharmacistDetails,
  ITEMS_PER_PAGE,
  successSelector,
} from '@pharmaplan/common';

import AdminPharmacistCard from '../../Profile/Pharmacist/PharmacistViewProfile/AdminPharmacistCard';
import useListOfPharmacies from '../../../../hooks/PharmacistList/useListOfPharmacies';
import {
  ICustomBack,
  renderScreen,
  setBack,
  setFooterShow,
} from '../../../../actions/drawerActions';
import NoAvailabilities from '../../MatchingAvailabilities/NoAvailabilities';
import { LocationText } from '../../AvailableWorkshifts/AvailableWorkshifts';
import CustomAvatarSection from '../../../common/CustomAvatarSection';
import useInfiniteScroll from '../../../../hooks/useInfiniteScroll';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import DrawerListHeader from '../../../common/DrawerListHeader';
import genericClasses from '../../../../theme/GenericClasses';
import useSelfService from '../../../../hooks/useSelfService';
import { ScreenTypes } from '../../../../helpers/Constants';
import useGetItems from '../../../../hooks/useGetItems';
import strings from '../../../../localization';

import styles from './styles';

interface IListOfPharmacies {
  pharmacistId: string;
  back?: ICustomBack;
}

const successAction = adminActions.listOfPharmacies;
const ListOfPharmacies: FC<IListOfPharmacies> = ({ pharmacistId, back }) => {
  const [page, setPage] = useState(1);
  const dispatch = useAppDispatch();

  const { isSelfService } = useSelfService();
  const { menuAction } = useListOfPharmacies();

  const sourceRef = useRef<Agencies>(
    isSelfService ? Agencies.PharmaGap : Agencies.PharmaPlan,
  );
  const listOfPharmacies = useAppSelector(adminListOfPharmacies);

  const loadSuccess = useAppSelector((state) =>
    successSelector([successAction], state));

  const { data } = listOfPharmacies ?? {};
  const { getImage } = useGetItems();

  const observerCallback = useCallback(
    (entries: { isIntersecting: any }[]) => {
      const paginationCondition = data.length >= page * ITEMS_PER_PAGE;

      if (paginationCondition && entries[0].isIntersecting) {
        setPage((prevPage) =>
          prevPage + 1);
      }
    },
    [page, data],
  );

  const { listEndRef } = useInfiniteScroll({ observerCallback });

  useEffect(() => {
    dispatch(
      adminGetListOfPharmacies({
        paging: {
          page: 1,
        },
        pharmacistId,
      }),
    );
    dispatch(getPharmacistDetails(pharmacistId));
    dispatch(setFooterShow(false));
    dispatch(setBack(back ?? null));
    return () => {
      dispatch(setBack(null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (page !== 1) {
      dispatch(
        adminGetListOfPharmaciesPagination({
          paging: {
            page,
          },
          source: sourceRef.current,
          pharmacistId,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const rowConfig = (replacements: string, hours: string, city: string) =>
    [
      {
        key: '1',
        value: `${strings.totalReplacements}: ${replacements}`,
      },
      {
        key: '2',
        value: `${strings.totalHours}: ${hours}`,
      },
      {
        key: '3',
        value: <LocationText label={city} />,
      },
    ];

  const ratingGoBack = () => {
    dispatch(
      renderScreen({
        screenNumber: 8,
        screenType: ScreenTypes.pharmacistList,
        pharmacistId,
      }),
    );
  };

  const handleFilterUpdate = (source: string) => {
    sourceRef.current = source as Agencies;
    dispatch(
      adminGetListOfPharmacies({
        paging: {
          page: 1,
        },
        source: source as Agencies,
        pharmacistId,
      }),
    );
  };

  return (
    <Box>
      <AdminPharmacistCard
        pharmacistId={pharmacistId}
        type={AdminLegendVariant.posted}
        ratingGoBack={ratingGoBack}
      />
      <DrawerListHeader
        showFilter={isSelfService}
        onFilterUpdate={handleFilterUpdate}
        title={strings.pharmacyList}
      />
      {data.length === 0 ? (
        <NoAvailabilities message={strings.noDataAvailable} />
      ) : (
        data.map((item) => {
          const {
            pharmacyName,
            pharmacyId,
            location,
            logoId,
            favourite,
            totalHours,
            totalWorkshift,
            isAutoSyncRecord,
          } = item ?? {};

          return (
            <Box key={pharmacyId} sx={styles.itemContainer}>
              <CustomAvatarSection
                customMenuContainerStyle={styles.menuContainer}
                imageSuccessAction={successAction}
                config={rowConfig(totalWorkshift, totalHours, location)}
                favorite={favourite}
                avatar={getImage(logoId)}
                title={pharmacyName}
                menuActions={menuAction({
                  pharmacistId,
                  pharmacyId,
                  isAutoSyncRecord: !!isAutoSyncRecord,
                })}
                successAction={successAction}
                showFullImage={false}
              />
            </Box>
          );
        })
      )}
      {loadSuccess && (
        <div ref={listEndRef} style={genericClasses.paginationDiv} />
      )}
    </Box>
  );
};

export default ListOfPharmacies;
